import React, { memo, useEffect, useState } from 'react';

import { State } from '@progress/kendo-data-query';
import { GridColumn, GridDataStateChangeEvent } from '@progress/kendo-react-grid';
import styled from 'styled-components';

import { ChangeLogModel } from 'models';

import { apiClient } from 'core/api/globals';
import { DataResult } from 'core/api/types';
import { useEvent } from 'core/hooks';
import { ComponentSizes, DEFAULT_DATA_TABLE_DATA_STATE, DataTable, DataTableVariants, DateCell, HeaderCellSecondary, TextCell } from 'core/ui';

interface ActivityLogTabProps {
  entityType: string;
  id: number;
}

const EMPTY_GRID_DATA: DataResult<ChangeLogModel> = {
  data: [],
  total: 0,
};

export const ActivityLogTab = memo<ActivityLogTabProps>(({ entityType, id }) => {
  const [dataState, setDataState] = useState(DEFAULT_DATA_TABLE_DATA_STATE);
  const [gridData, setGridData] = useState<DataResult<ChangeLogModel>>(EMPTY_GRID_DATA);
  const [isLoading, setIsLoading] = useState(false);

  const refreshData = useEvent(async (newDataState?: State) => {
    // Only fetch if id is valid (greater than 0 for existing exams)
    if (id && id > 0) {
      try {
        setIsLoading(true);
        setGridData(await apiClient.changeLogsClient.getLogs(newDataState ?? dataState, entityType, id));
      } finally {
        setIsLoading(false);
      }
    } else {
      // Reset data if id is invalid (e.g., new exam)
      setGridData(EMPTY_GRID_DATA);
    }
  });

  const handleDataStateChange = useEvent((event: GridDataStateChangeEvent) => {
    setDataState(event.dataState);
    refreshData(event.dataState);
  });

  // Refresh data when id changes (e.g., after saving a new exam)
  useEffect(() => {
    refreshData();
  }, [refreshData, entityType, id]);

  return (
    <StyledTabContent>
      {id && id > 0 ? (
        <StyledDataTable
          {...dataState}
          data={gridData.data}
          total={gridData.total}
          pageable
          reorderable
          size={ComponentSizes.SMALL}
          sortable
          variant={DataTableVariants.SECONDARY}
          onDataStateChange={handleDataStateChange}
          isLoading={isLoading}
        >
          <GridColumn cell={TextCell} field="propertyName" filterable={false} title="Property Name" width="200px" headerCell={HeaderCellSecondary} />
          <GridColumn cell={TextCell} field="oldValue" filterable={false} title="Old Value" width="200px" headerCell={HeaderCellSecondary} />
          <GridColumn cell={TextCell} field="newValue" filterable={false} title="New Value" width="200px" headerCell={HeaderCellSecondary} />
          <GridColumn cell={TextCell} field="userModified" filterable={false} title="User" width="200px" headerCell={HeaderCellSecondary} />
          <GridColumn cell={DateCell} field="dateModified" filterable={false} title="Date" width="150px" headerCell={HeaderCellSecondary} />
        </StyledDataTable>
      ) : (
        <CenteredMessage>Activity log available after exam is saved.</CenteredMessage>
      )}
    </StyledTabContent>
  );
});

ActivityLogTab.displayName = 'ActivityLogTab';

const StyledTabContent = styled.div`
  padding: 15px; /* Add padding around the tab content */
  height: 100%;
  overflow-y: auto; /* Allow scrolling if content exceeds height */
`;

const StyledDataTable = styled(DataTable)`
  margin: 0;
  min-height: 150px; /* Ensure minimum height */
  border: 1px solid ${({ theme }) => theme.colors.palette.grayscale[300]}; /* Use palette color for border */
`;

const CenteredMessage = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-style: italic;
`;
